'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type VerificationStatus = 'verifying' | 'success' | 'error';

export function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<VerificationStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      const tokenHash = searchParams.get('token_hash');
      const type = searchParams.get('type') || 'signup';

      if (!tokenHash) {
        setStatus('error');
        setErrorMessage('Invalid confirmation link');
        return;
      }

      try {
        const { error } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: type as 'signup' | 'recovery' | 'email_change' | 'invite',
        });

        if (error) {
          setStatus('error');
          setErrorMessage(error.message);
          return;
        }

        // Success - email verified
        setStatus('success');

        // Attempt to open the app after a short delay
        setTimeout(() => {
          window.location.href = 'quappstore://auth/verified';
        }, 1500);

      } catch (err) {
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'Verification failed');
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <main className="confirm-page" role="main">
      <article className="confirm-card" aria-live="polite">
        {/* Minimal Branding */}
        <header className="brand">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="brand-icon">
            <rect width="32" height="32" rx="8" fill="#FF9500"/>
            <path d="M8 16L14 22L24 10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="brand-text">Quapp  - Email Verification</span>
        </header>

        {/* Verifying State */}
        {status === 'verifying' && (
          <section className="content" aria-label="Verifying">
            <div className="spinner" aria-hidden="true" />
            <h1>Verifying your email</h1>
            <p>Please wait a moment...</p>
          </section>
        )}

        {/* Success State */}
        {status === 'success' && (
          <section className="content" aria-label="Success">
            <div className="icon success-icon" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#22c55e"/>
                <path d="M14 24L21 31L34 17" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h1>Email Confirmed!</h1>
            <p>Your account has been verified successfully.</p>
            <p className="hint">Return to the app to continue.</p>
            <a href="quappstore://auth/verified" className="button">
              Open App
            </a>
          </section>
        )}

        {/* Error State */}
        {status === 'error' && (
          <section className="content" aria-label="Error">
            <div className="icon error-icon" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <circle cx="24" cy="24" r="24" fill="#ef4444"/>
                <path d="M16 16L32 32M32 16L16 32" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <h1>Verification Failed</h1>
            <p className="error-text">{errorMessage || 'The link may have expired or already been used.'}</p>
            <p className="hint">Please request a new confirmation email from the app.</p>
          </section>
        )}
      </article>

      <style>{`
        .confirm-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%);
        }

        .confirm-card {
          background: #ffffff;
          border-radius: 20px;
          padding: 40px 32px;
          max-width: 380px;
          width: 100%;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
        }

        .brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 1px solid #f1f1f1;
        }

        .brand-icon {
          flex-shrink: 0;
        }

        .brand-text {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a2e;
          letter-spacing: -0.3px;
        }

        .content {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #FF9500;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 24px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .icon {
          margin-bottom: 20px;
        }

        h1 {
          font-size: 22px;
          font-weight: 700;
          color: #1a1a2e;
          margin: 0 0 12px 0;
          letter-spacing: -0.5px;
        }

        p {
          font-size: 15px;
          color: #64748b;
          margin: 0 0 8px 0;
          line-height: 1.5;
        }

        .error-text {
          color: #dc2626;
          font-weight: 500;
        }

        .hint {
          font-size: 13px;
          color: #94a3b8;
          margin-top: 8px;
        }

        .button {
          display: inline-block;
          margin-top: 24px;
          padding: 14px 32px;
          background: #FF9500;
          color: white;
          font-size: 15px;
          font-weight: 600;
          text-decoration: none;
          border-radius: 12px;
          transition: all 0.2s ease;
        }

        .button:hover {
          background: #E68600;
          transform: translateY(-1px);
        }

        .button:active {
          transform: translateY(0);
        }

        @media (max-width: 420px) {
          .confirm-card {
            padding: 32px 24px;
          }

          h1 {
            font-size: 20px;
          }
        }
      `}</style>
    </main>
  );
}

